import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Ban, Clock, Download, Eye, Loader2, PenLine, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cancelSigning, pollSigningStatus } from "@/domains/documents/signing/repository";
import { useContainerContents, useSessionDocuments } from "../useSigningItems";
import { useFileDownload } from "../useFileDownload";
import { useSigningPreview } from "../signingPreviewContext";
import { refusalMessage } from "../refusalMessage";
import { isWaitingForYou, signerProgress } from "../status";
import type { SigningListItem } from "../types";
import { SignatureList } from "./SignatureList";
import { SigningFileList } from "./SigningFileList";
import { SigningPanelHeader } from "./SigningPanelHeader";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface SigningDetailPanelProps {
  item: SigningListItem;
  /**
   * Open the sign dialog for a session. The dialog itself is mounted by
   * SigningContent, ABOVE the selection: finishing a turn completes the session,
   * a completed session leaves the list, and this panel is unmounted with it —
   * so a dialog owned here would vanish before it could say what happened.
   */
  onSignTurn: (sessionId: string) => void;
}

// The sheet it sits in supplies the chrome and the top padding.
const PANEL_CLASS = "flex flex-col gap-5 px-5 pb-5";
const SECTION_HEADING =
  "mb-3 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground";

/**
 * The right-hand panel for the selected row. Every field comes from a real row:
 * a finished container shows its signature trail, an in-flight chain shows the
 * whole signer stepper plus the two actions the viewer may actually take —
 * signing their own turn, and (for the sender) calling the chain off.
 */
export function SigningDetailPanel({ item, onSignTurn }: SigningDetailPanelProps) {
  // key: both panels render at the same position, so without it React keeps
  // SessionPanel's isChecking state when the selection moves between sessions —
  // row B would show row A's in-flight spinner.
  return item.kind === "container" ? (
    <ContainerPanel item={item} />
  ) : (
    <SessionPanel key={item.key} item={item} onSignTurn={onSignTurn} />
  );
}

/** A finished container: its signature trail, a download, and its contents. */
function ContainerPanel({ item }: { item: SigningListItem }) {
  const { t } = useLanguage();
  const contents = useContainerContents(item.documentId);
  const download = useFileDownload();
  const preview = useSigningPreview();
  // A signed container's files are opened from the contents list below; a
  // signed file the viewer renders (a PDF) opens from here.
  const canOpen = preview != null && !preview.isContainerItem(item) && preview.canOpenItem(item);

  return (
    <aside className={PANEL_CLASS}>
      <SigningPanelHeader item={item} />

      <section>
        <h3 className={SECTION_HEADING}>{t.signing.panel.signatures}</h3>
        <SignatureList signers={item.signers} fallbackName={item.senderName} />
      </section>

      {canOpen && (
        <Button
          variant="outline"
          size="control"
          disabled={preview.openingId === item.documentId}
          onClick={() => preview.openItem(item)}
        >
          {preview.openingId === item.documentId ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <Eye aria-hidden="true" />
          )}
          {t.documents.signedContainer.panel.open}
        </Button>
      )}

      <Button
        {...pageInfoTarget("signing.download")}
        size="control"
        disabled={!item.filePath}
        onClick={() => download(item.filePath, item.title)}
      >
        <Download aria-hidden="true" />
        {t.signing.panel.download}
      </Button>

      <SigningFileList
        title={t.signing.panel.containerContents}
        files={contents.data ?? []}
        isLoading={contents.isLoading}
        isError={contents.isError}
      />
    </aside>
  );
}

/**
 * Whether the chain is still waiting on a person — the only states the
 * "waiting" hint and the Sign button make sense in.
 *
 * `finalizing` is deliberately NOT one of them: nobody is pending, so there is
 * nobody to wait for and no turn to take.
 */
function isInFlight(item: SigningListItem): boolean {
  return item.status === "awaiting_you" || item.status === "awaiting_others";
}

/**
 * Whether calling the chain off is still worth OFFERING — a wider set than
 * {@link isInFlight}, because `finalizing` is not the same as fully signed.
 *
 * It means "at least one signature, nobody pending" (see sessionItemStatus), a
 * shape a chain also reaches when its remaining signers DECLINED. That chain is
 * cancellable, and so is one whose finalize got stuck — both polls give up
 * after 2 minutes, and the sender would otherwise be left with no action at all.
 * When the chain really is fully signed the server says so (409 `fully_signed`)
 * and confirmCancel reports it, which beats hiding the button on a guess.
 */
function canStillCancel(item: SigningListItem): boolean {
  return isInFlight(item) || item.status === "finalizing";
}

/** An open (or ended) chain: who it is waiting for, and what you can do about it. */
function SessionPanel({
  item,
  onSignTurn,
}: {
  item: SigningListItem;
  onSignTurn: (sessionId: string) => void;
}) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const { isPlatformAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [isChecking, setIsChecking] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const documents = useSessionDocuments(item.sessionId, item.sourceDocumentIds);

  const sessionId = item.sessionId;
  const progress = signerProgress(item.signers);
  const inFlight = isInFlight(item);
  const yourTurn = inFlight && isWaitingForYou(item.signers);
  // `finalizing` is "nobody is pending", which a chain also reaches when its
  // remaining signers DECLINED. "Every signature is in" about that chain is
  // false whatever the backend does next, so the sentence says what is actually
  // known — the status itself stays `finalizing`, because that is what drives
  // the reconcile that resolves it (useSigningAutoRefresh).
  const declined = item.signers.some((signer) => signer.status === "rejected");
  // Mirrors dokobit-sign-cancel's own authz: the sender, or a platform admin.
  // A signer declines inside Dokobit rather than killing someone else's chain.
  const canCancel =
    canStillCancel(item) &&
    (isPlatformAdmin || (userProfile?.id != null && item.senderUserId === userProfile.id));

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["signing"] });

  const checkStatus = async () => {
    if (!item.sessionId) return;
    setIsChecking(true);
    try {
      await pollSigningStatus(item.sessionId);
      // Invalidating the "signing" prefix refetches every active list/detail
      // query on the page — no extra refetch call on top.
      await refresh();
    } catch (error) {
      toast({
        title: refusalMessage(error, t.signing, t.signing.loadFailed),
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  const confirmCancel = async () => {
    if (!item.sessionId) return;
    try {
      await cancelSigning(item.sessionId);
      toast({ title: t.signing.panel.cancelled });
    } catch (error) {
      // A 409 (finalizing / fully_signed) is a real answer, not a bug: the
      // chain won the race. The refresh below shows where it actually landed.
      toast({
        title: refusalMessage(error, t.signing, t.signing.panel.signTurnFailed),
        variant: "destructive",
      });
    } finally {
      await refresh();
    }
  };

  return (
    <aside className={PANEL_CLASS}>
      <SigningPanelHeader item={item} />

      <section>
        <h3 className={SECTION_HEADING}>
          {t.signing.chain.signers}
          {progress.total > 0 && (
            <span className="ml-2 font-semibold normal-case tracking-normal">
              {t.signing.chain.progress
                .replace("{signed}", String(progress.signed))
                .replace("{total}", String(progress.total))}
            </span>
          )}
        </h3>
        <SignatureList signers={item.signers} fallbackName={item.senderName} />
      </section>

      <section className="flex flex-col gap-3">
        {inFlight && !yourTurn && item.signers.length > 0 && (
          <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
            <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t.signing.panel.waitingForOthers}
          </p>
        )}

        {item.status === "finalizing" && (
          <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
            <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
            {declined ? t.signing.panel.finalizingAfterDecline : t.signing.panel.finalizing}
          </p>
        )}

        {yourTurn && sessionId && (
          <Button
            {...pageInfoTarget("signing.signNow")}
            size="control"
            onClick={() => onSignTurn(sessionId)}
          >
            <PenLine aria-hidden="true" />
            {t.signing.panel.signNow}
          </Button>
        )}

        <Button
          {...pageInfoTarget("signing.checkStatus")}
          variant="outline"
          size="control"
          onClick={checkStatus}
          disabled={isChecking}
        >
          {isChecking ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw aria-hidden="true" />
          )}
          {t.signing.panel.checkStatus}
        </Button>

        {canCancel && (
          <Button variant="outline" size="control" onClick={() => setIsCancelOpen(true)}>
            <Ban aria-hidden="true" />
            {t.signing.panel.cancelSigning}
          </Button>
        )}
      </section>

      <SigningFileList
        title={t.signing.panel.documentsToSign}
        files={documents.data ?? []}
        isLoading={documents.isLoading}
        isError={documents.isError}
      />

      <ConfirmDialog
        open={isCancelOpen}
        onOpenChange={setIsCancelOpen}
        onConfirm={confirmCancel}
        title={t.signing.panel.cancelConfirmTitle}
        description={t.signing.panel.cancelConfirmBody}
        confirmText={t.signing.panel.cancelConfirmAction}
        cancelText={t.signing.panel.cancelKeep}
      />
    </aside>
  );
}
